import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import {
  Check, X, Loader2, Lock, Trophy, ArrowLeft, Thermometer, Sparkles, Crown,
  Wallet, AlertTriangle, MapPin, Plane, Home, ShieldPlus, CalendarCheck, FileDown, Quote,
  Globe2, Compass, Route, FileText, Users, Scale, ListChecks,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const ANALYZING_MESSAGES = [
  "Analyzing your lifestyle…", "Consulting our AI relocation advisor…",
  "Weighing your priorities across 31 cities…", "Scoring climate, cost, safety & career…",
  "Writing your personalized recommendations…",
];

function AnalyzingState() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % ANALYZING_MESSAGES.length), 1600);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="analyzing-screen" className="flex min-h-screen flex-col items-center justify-center bg-white px-6">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary">
        <Globe2 className="h-8 w-8 text-white animate-spin" style={{ animationDuration: "3s" }} />
      </div>
      <h2 className="mt-8 font-heading text-2xl font-semibold tracking-tight text-center">{ANALYZING_MESSAGES[idx]}</h2>
      <p className="mt-2 text-sm text-slate-500">Generating your AI-personalized report — this can take up to a minute.</p>
      <div className="mt-8 h-1 w-64 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full w-1/2 animate-pulse rounded-full bg-primary" />
      </div>
    </div>
  );
}

function ScoreRing({ score }) {
  const r = 52, c = 2 * Math.PI * r;
  const off = c - (score / 100) * c;
  return (
    <div className="relative h-32 w-32">
      <svg className="h-32 w-32 -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#EAEAEA" strokeWidth="8" />
        <circle cx="60" cy="60" r={r} fill="none" stroke="#111111" strokeWidth="8" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 1s ease-out" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-3xl font-semibold">{score}%</span>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">Match</span>
      </div>
    </div>
  );
}

const DIM_LABELS = {
  lifestyle: "Lifestyle", cost_of_living: "Affordability", career: "Career", safety: "Safety",
  healthcare: "Healthcare", education: "Education", climate: "Climate", taxes: "Taxes", housing: "Housing",
};

function DimensionScores({ scores, rank }) {
  if (!scores) return null;
  return (
    <div className="mt-4 grid grid-cols-3 gap-x-4 gap-y-2.5" data-testid={`dimension-scores-${rank}`}>
      {Object.entries(scores).map(([k, v]) => (
        <div key={k}>
          <div className="flex items-center justify-between text-[11px] text-slate-500">
            <span>{DIM_LABELS[k] || k}</span><span className="font-semibold text-slate-700">{v}</span>
          </div>
          <div className="mt-1 h-1 w-full rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-primary" style={{ width: `${v}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function CityCard({ city, rank }) {
  return (
    <div data-testid={`city-card-${rank}`} className="overflow-hidden rounded-lg border border-slate-200 bg-white transition-all duration-200 hover:border-slate-300">
      <div className="relative h-40">
        <img src={city.image_url} alt={city.name} className="h-full w-full object-cover" />
        <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-md bg-white/95 px-2 py-1 text-xs font-semibold backdrop-blur">
          {rank === 1 && <Crown className="h-3.5 w-3.5 text-amber-500" />} #{rank}
        </div>
        <div className="absolute right-3 top-3 rounded-md bg-primary px-2.5 py-1 text-sm font-semibold text-white">{city.match_score}%</div>
      </div>
      <div className="p-5">
        <h3 className="font-heading text-xl font-semibold tracking-tight">{city.name}</h3>
        <p className="text-sm text-slate-500">{city.country}</p>

        {city.best_for && (
          <div className="mt-2 flex items-start gap-1.5 text-xs text-slate-600" data-testid={`city-best-for-${rank}`}>
            <Users className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /><span>{city.best_for}</span>
          </div>
        )}

        {city.explanation && (
          <div className="mt-3 flex items-start gap-2 rounded-md bg-slate-50 p-3 text-sm leading-relaxed text-slate-700">
            <Quote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span data-testid={`city-explanation-${rank}`}>{city.explanation}</span>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm">
          <span className="flex items-center gap-1.5 text-slate-500"><Wallet className="h-3.5 w-3.5" /> Est. monthly budget</span>
          <span className="font-semibold">${city.est_monthly_budget?.toLocaleString()}</span>
        </div>

        <DimensionScores scores={city.dimension_scores} rank={rank} />

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-600">Pros</div>
            <ul className="space-y-1.5">
              {(city.pros || []).map((p) => (<li key={p} className="flex items-start gap-2 text-sm text-slate-700"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" /> {p}</li>))}
            </ul>
          </div>
          <div>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-rose-600">Cons</div>
            <ul className="space-y-1.5">
              {(city.cons || []).map((p) => (<li key={p} className="flex items-start gap-2 text-sm text-slate-700"><X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-400" /> {p}</li>))}
            </ul>
          </div>
        </div>

        {city.immigration_notes && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-slate-100 bg-slate-50 p-2.5 text-xs text-slate-600">
            <Plane className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span data-testid={`city-immigration-${rank}`}>{city.immigration_notes}</span>
          </div>
        )}

        {city.caution && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-100 bg-amber-50/60 p-2.5 text-xs text-amber-800" data-testid={`city-caution-${rank}`}>
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{city.caution}</span>
          </div>
        )}

        {city.tradeoffs?.length > 0 && (
          <div className="mt-3 flex items-start gap-2 text-xs text-amber-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>Key tradeoff: {city.tradeoffs.join("; ")}.</span>
          </div>
        )}

        {city.lifestyle_reasons?.length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-4">
            <div className="mb-1.5 flex items-center gap-1.5 text-xs text-slate-500"><Sparkles className="h-3.5 w-3.5" /> Why it fits your lifestyle</div>
            <ul className="space-y-1">
              {city.lifestyle_reasons.map((r) => (<li key={r} className="text-xs text-slate-500">• {r}</li>))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-500" />
        <h3 className="font-heading text-lg font-semibold tracking-tight">{title}</h3>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function PersonalizationPanel({ weights, explanation, facts }) {
  if (!weights?.length) return null;
  return (
    <div className="mt-8 rounded-lg border border-slate-200 bg-white p-6" data-testid="personalization-panel">
      <div className="flex items-center gap-2">
        <Compass className="h-4 w-4 text-slate-500" />
        <h3 className="font-heading text-lg font-semibold tracking-tight">What shaped your matches</h3>
      </div>
      <p className="mt-1 text-sm text-slate-500">Your answers set a unique priority profile — here's how much each factor weighed in your scoring.</p>
      {facts?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="user-facts">
          {facts.map((f) => (<Badge key={f} variant="secondary" className="font-normal">{f}</Badge>))}
        </div>
      )}
      <div className="mt-5 grid gap-x-8 gap-y-3.5 sm:grid-cols-2">
        {weights.map((w) => (
          <div key={w.key} data-testid={`weight-${w.key}`}>
            <div className="mb-1 flex items-center justify-between text-sm">
              <span className="text-slate-600">{w.label}</span>
              <span className="font-semibold text-slate-800">{w.percent}%</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-primary" style={{ width: `${w.percent}%` }} />
            </div>
          </div>
        ))}
      </div>
      {explanation && <p className="mt-5 text-sm leading-relaxed text-slate-500" data-testid="weights-explanation">{explanation}</p>}
    </div>
  );
}

function RejectedCard({ city }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`not-recommended-${city.name}`}>
      <div className="flex items-center justify-between">
        <div className="font-medium">{city.name} <span className="text-sm font-normal text-slate-400">{city.country}</span></div>
        <span className="text-sm font-semibold text-slate-500">{city.score}% match</span>
      </div>
      {city.reasons?.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {city.reasons.map((r) => (
            <li key={r} className="flex items-start gap-2 text-sm text-slate-600">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
              <span className="first-letter:uppercase">{r}</span>
            </li>
          ))}
        </ul>
      )}
      {city.tradeoffs?.length > 0 && (
        <p className="mt-2 text-xs leading-relaxed text-slate-500">{city.tradeoffs.join(" ")}</p>
      )}
    </div>
  );
}

function NotRecommendedTeaser({ cities }) {
  if (!cities?.length) return null;
  return (
    <div className="mt-8 rounded-lg border border-slate-200 bg-white p-6" data-testid="not-recommended-teaser">
      <div className="flex items-center gap-2">
        <Compass className="h-4 w-4 text-slate-500" />
        <h3 className="font-heading text-lg font-semibold tracking-tight">Cities that didn't make your top list</h3>
      </div>
      <p className="mt-1 text-sm text-slate-500">Popular destinations you might have expected — and exactly why they ranked lower for you.</p>
      <div className="mt-4 grid gap-4">
        {cities.map((c) => (<RejectedCard key={c.name} city={c} />))}
      </div>
      <div className="mt-4 flex items-start gap-2.5 rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <span>Unlock the full report to see every destination that didn't make your shortlist and the trade-offs behind each.</span>
      </div>
    </div>
  );
}

function ComparisonTable({ data }) {
  if (!data?.rows?.length) return null;
  return (
    <Section icon={Scale} title="How your top matches compare" testid="basic-comparison">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
            <th className="py-2 pr-3">Factor</th>
            {data.cities.map((c) => <th key={c} className="py-2 pr-3">{c}</th>)}
          </tr></thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.label} className="border-b border-slate-100">
                <td className="py-2.5 pr-3 font-medium text-slate-600">{row.label}</td>
                {row.values.map((v, i) => (
                  <td key={i} className="py-2.5 pr-3">
                    {row.type === "score" ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="font-semibold">{v}</span>
                        <span className="h-1.5 w-12 overflow-hidden rounded-full bg-slate-100">
                          <span className="block h-full rounded-full bg-primary" style={{ width: `${v}%` }} />
                        </span>
                      </span>
                    ) : (
                      <span className="text-slate-700">{v}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function BudgetSnapshot({ data }) {
  if (!data?.items?.length) return null;
  const money = (n) => `$${Number(n).toLocaleString()}`;
  return (
    <Section icon={Wallet} title={`Budget snapshot · ${data.city}`} testid="basic-budget-snapshot">
      <div className="space-y-2.5">
        {data.items.map((it) => (
          <div key={it.label} className="flex items-center justify-between border-b border-slate-100 pb-2 text-sm">
            <span className="text-slate-600">{it.label}</span>
            {it.note ? <span className="text-right text-slate-500">{it.note}</span>
              : <span className="font-medium">{money(it.low)} – {money(it.high)}</span>}
          </div>
        ))}
        <div className="flex items-center justify-between pt-1 text-sm">
          <span className="font-semibold">Approx. monthly total</span>
          <span className="font-semibold">{money(data.monthly_total.low)} – {money(data.monthly_total.high)}</span>
        </div>
      </div>
      <p className="mt-3 text-xs text-slate-400">{data.note}</p>
    </Section>
  );
}

function NextSteps({ steps }) {
  if (!steps?.length) return null;
  return (
    <Section icon={ListChecks} title="Your personalized next steps" testid="basic-next-steps">
      <ol className="space-y-2.5">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">{i + 1}</span>
            {s}
          </li>
        ))}
      </ol>
    </Section>
  );
}

function FullReport({ fr, onDownload, downloading }) {
  const breakdownEntries = Object.entries(fr.cost_breakdown || {});
  const neighborhoodEntries = Object.entries(fr.neighborhoods || {});
  return (
    <div className="mt-6 space-y-6 animate-fade-up" data-testid="full-report">
      <ComparisonTable data={fr.comparison} />
      <div className="grid gap-6 lg:grid-cols-2">
        <BudgetSnapshot data={fr.budget_snapshot} />
        <NextSteps steps={fr.next_steps} />
      </div>

      <Section icon={Trophy} title="Top 10 city ranking" testid="full-top10">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="py-2 pr-3">#</th><th className="py-2 pr-3">City</th><th className="py-2 pr-3">Match</th>
              <th className="py-2 pr-3">Est. budget</th><th className="py-2 pr-3">Safety</th><th className="py-2 pr-3">Healthcare</th><th className="py-2">QoL</th>
            </tr></thead>
            <tbody>
              {fr.all_cities.map((c, i) => (
                <tr key={c.name} className="border-b border-slate-100">
                  <td className="py-2.5 pr-3 font-semibold">{i + 1}</td>
                  <td className="py-2.5 pr-3"><span className="font-medium">{c.name}</span> <span className="text-slate-400">{c.country}</span></td>
                  <td className="py-2.5 pr-3 font-semibold">{c.match_score}%</td>
                  <td className="py-2.5 pr-3">${c.est_monthly_budget?.toLocaleString()}</td>
                  <td className="py-2.5 pr-3">{c.metrics.safety}</td>
                  <td className="py-2.5 pr-3">{c.metrics.healthcare}</td>
                  <td className="py-2.5">{c.metrics.quality_of_life}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {(breakdownEntries.length > 0 || neighborhoodEntries.length > 0) && (
      <div className="grid gap-6 lg:grid-cols-2">
        {breakdownEntries.length > 0 && (
        <Section icon={Wallet} title="Cost breakdown" testid="full-cost-breakdown">
          <div className="space-y-5">
            {breakdownEntries.map(([city, b]) => (
              <div key={city}>
                <div className="mb-2 flex items-center justify-between"><span className="font-medium">{city}</span><span className="font-semibold">${b.total.toLocaleString()}/mo</span></div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-slate-600">
                  <span>Rent</span><span className="text-right">${b.rent.toLocaleString()}</span>
                  <span>Food</span><span className="text-right">${b.food.toLocaleString()}</span>
                  <span>Transport</span><span className="text-right">${b.transport.toLocaleString()}</span>
                  <span>Utilities</span><span className="text-right">${b.utilities.toLocaleString()}</span>
                  <span>Leisure</span><span className="text-right">${b.leisure.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </Section>
        )}

        {neighborhoodEntries.length > 0 && (
        <Section icon={MapPin} title="Neighborhood suggestions" testid="full-neighborhoods">
          <div className="space-y-5">
            {Object.entries(fr.neighborhoods || {}).map(([city, list]) => (
              <div key={city}>
                <div className="mb-2 font-medium">{city}</div>
                <ul className="space-y-1.5">
                  {list.map((n) => (<li key={n.name} className="text-sm text-slate-600"><span className="font-medium text-slate-800">{n.name}</span> — {n.blurb}</li>))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
        )}
      </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {fr.visa_overview && <Section icon={Plane} title="Visa & immigration" testid="full-visa"><p className="text-sm leading-relaxed text-slate-600">{fr.visa_overview}</p></Section>}
        {fr.housing_guidance && <Section icon={Home} title="Housing guidance" testid="full-housing"><p className="text-sm leading-relaxed text-slate-600">{fr.housing_guidance}</p></Section>}
        {fr.safety_healthcare_notes && <Section icon={ShieldPlus} title="Safety & healthcare" testid="full-safety-health"><p className="text-sm leading-relaxed text-slate-600">{fr.safety_healthcare_notes}</p></Section>}
      </div>

      {fr.immigration_notes && Object.keys(fr.immigration_notes).length > 0 && (
        <Section icon={Plane} title="Immigration notes by city" testid="full-immigration">
          <div className="space-y-3">
            {Object.entries(fr.immigration_notes).map(([city, note]) => (
              <div key={city} className="text-sm text-slate-600"><span className="font-medium text-slate-800">{city}</span> — {note}</div>
            ))}
          </div>
        </Section>
      )}

      {fr.recommended_strategy && (
        <Section icon={Route} title="Recommended relocation strategy" testid="full-strategy">
          <p className="text-sm leading-relaxed text-slate-600">{fr.recommended_strategy}</p>
        </Section>
      )}

      {fr.plan_90_day?.length > 0 && (
        <Section icon={CalendarCheck} title="Your 90-day relocation plan" testid="full-plan">
          <div className="grid gap-5 md:grid-cols-3">
            {fr.plan_90_day.map((p) => (
              <div key={p.phase} className="rounded-lg border border-slate-200 p-4">
                <div className="mb-3 font-medium">{p.phase}</div>
                <ul className="space-y-2">
                  {p.items.map((it) => (<li key={it} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" /> {it}</li>))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      {fr.not_recommended?.length > 0 && (
        <Section icon={Compass} title="Cities that didn't make your shortlist" testid="full-not-recommended">
          <p className="mb-4 text-sm text-slate-500">Strong destinations you might have expected — and the specific reasons they ranked below your top matches, given what you care about most.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {fr.not_recommended.map((c) => (<RejectedCard key={c.name} city={c} />))}
          </div>
        </Section>
      )}

      <div className="flex justify-center">
        <Button variant="outline" className="gap-2" data-testid="download-pdf-btn" disabled={downloading} onClick={onDownload}>
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Download PDF report
        </Button>
      </div>
    </div>
  );
}

export default function Results() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [report, setReport] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  const hasKit = user?.plan === "success_kit";

  const startUpgrade = async () => {
    setUpgrading(true);
    try {
      const { data } = await api.post(`/payments/checkout`, {
        package_id: "kit_upgrade",
        origin_url: window.location.origin,
        report_id: reportId,
      });
      window.location.href = data.url;
    } catch {
      toast.error("Could not start upgrade checkout");
      setUpgrading(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/reports/${reportId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `canhelpmove-report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    let active = true;
    let timer;
    const load = async () => {
      try {
        const { data } = await api.get(`/reports/${reportId}`);
        if (!active) return;
        setReport(data);
        if (data.status === "processing") {
          timer = setTimeout(load, 3000);
        }
      } catch {
        if (active) { toast.error("Report not found"); navigate("/reports"); }
      }
    };
    load();
    return () => { active = false; if (timer) clearTimeout(timer); };
  }, [reportId, navigate]);

  // Returning from Stripe checkout — verify payment, then unlock the report.
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    let active = true;
    let attempts = 0;
    setVerifyingPayment(true);
    const poll = async () => {
      if (!active) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const { data: fresh } = await api.get(`/reports/${reportId}`);
          if (!active) return;
          setReport(fresh);
          setVerifyingPayment(false);
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          if (data.package_id === "kit_upgrade" || data.package_id === "success_kit") {
            await refreshUser();
            toast.success("Relocation Success Kit activated! Your AI concierge is ready.");
            navigate("/success-kit");
          } else {
            toast.success("Payment successful — full report unlocked!");
            setTimeout(() => document.getElementById("full-report-anchor")?.scrollIntoView({ behavior: "smooth" }), 300);
            if (user?.plan !== "success_kit") setTimeout(() => setShowUpsell(true), 900);
          }
          return;
        }
        if (data.status === "expired") {
          if (!active) return;
          setVerifyingPayment(false);
          toast.error("Payment session expired. Please try again.");
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          return;
        }
        attempts += 1;
        if (attempts < 8) setTimeout(poll, 2000);
        else { setVerifyingPayment(false); toast.info("Still processing your payment. Refresh in a moment."); }
      } catch {
        if (active) { setVerifyingPayment(false); toast.error("Could not verify payment."); }
      }
    };
    poll();
    return () => { active = false; };
    // Deliberately keyed to [reportId] only: this is a one-shot poll for the
    // session_id present at mount. Adding searchParams/user/refreshUser would
    // restart the poll mid-verification and duplicate API calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const unlock = async () => {
    setUnlocking(true);
    try {
      const { data } = await api.post(`/payments/checkout`, {
        package_id: "report_unlock",
        origin_url: window.location.origin,
        report_id: reportId,
      });
      window.location.href = data.url;
    } catch {
      toast.error("Could not start checkout");
      setUnlocking(false);
    }
  };

  if (!report) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
  if (report.status === "processing") return <AnalyzingState />;
  if (report.status === "failed") {
    return (
      <div className="mx-auto max-w-lg px-6 py-24 text-center">
        <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" />
        <h1 className="mt-4 font-heading text-2xl font-semibold">We couldn't generate your report</h1>
        <p className="mt-2 text-slate-500">Please try the assessment again.</p>
        <Button className="mt-6" onClick={() => navigate("/assessment")}>Retake assessment</Button>
      </div>
    );
  }

  const costData = report.top_cities.map((c) => ({ name: c.name, rent: c.avg_rent_1br }));

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 animate-fade-up">
      <Button variant="ghost" size="sm" onClick={() => navigate("/reports")} className="mb-4 gap-2 text-slate-500" data-testid="back-to-reports">
        <ArrowLeft className="h-4 w-4" /> All reports
      </Button>

      <div className="flex flex-col items-start gap-8 rounded-lg border border-slate-200 bg-white p-8 sm:flex-row sm:items-center">
        <ScoreRing score={report.overall_match_score} />
        <div className="flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="gap-1.5"><Trophy className="h-3 w-3" /> Your relocation matches</Badge>
            {report.ai_generated && <Badge className="gap-1.5 bg-primary"><Sparkles className="h-3 w-3" /> AI-generated</Badge>}
          </div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">{report.top_cities[0]?.name} is your top match</h1>
          {report.executive_summary ? (
            <p className="mt-2 text-slate-600 leading-relaxed" data-testid="executive-summary">{report.executive_summary}</p>
          ) : (
            <p className="mt-2 text-slate-500">We scored 31 cities against your priorities. Here are your top 3 — unlock the full report for the top 10 and a complete relocation plan.</p>
          )}
        </div>
      </div>

      <div className="mt-8 grid gap-5 lg:grid-cols-3">
        {report.top_cities.map((city, i) => (<CityCard key={city.name} city={city} rank={i + 1} />))}
      </div>

      <PersonalizationPanel weights={report.weights_breakdown} explanation={report.weights_explanation} facts={report.user_facts} />

      <div className="mt-8 grid gap-5 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="cost-comparison">
          <h3 className="font-heading text-lg font-semibold tracking-tight">Cost comparison</h3>
          <p className="text-sm text-slate-500">Average 1-bedroom rent per month</p>
          <div className="mt-4 h-48">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <BarChart data={costData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#666" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#999" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#f5f5f5" }} formatter={(v) => [`$${v}`, "Rent/mo"]} />
                <Bar dataKey="rent" radius={[4, 4, 0, 0]}>
                  {costData.map((entry, i) => <Cell key={entry.name || `cell-${i}`} fill={i === 0 ? "#111111" : "#cfcfcf"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="climate-comparison">
          <h3 className="font-heading text-lg font-semibold tracking-tight">Climate comparison</h3>
          <p className="text-sm text-slate-500">Relative warmth index</p>
          <div className="mt-4 space-y-4">
            {report.top_cities.map((c) => (
              <div key={c.name}>
                <div className="mb-1.5 flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5"><Thermometer className="h-3.5 w-3.5 text-slate-400" /> {c.name}</span>
                  <span className="text-slate-500">{c.climate}</span>
                </div>
                <Progress value={c.climate_warmth} className="h-2" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {!report.unlocked && !hasKit && (
        <NotRecommendedTeaser cities={report.not_recommended_teaser} />
      )}

      <div id="full-report-anchor" />
      {(report.unlocked || hasKit) && report.full_report?.all_cities ? (
        <>
          <FullReport fr={report.full_report} onDownload={downloadPdf} downloading={downloading} />
          {!hasKit && (
            <div className="mt-8 flex flex-col items-start gap-4 rounded-lg border border-slate-200 bg-slate-50 p-6 sm:flex-row sm:items-center" data-testid="kit-upsell-banner">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary"><Route className="h-5 w-5 text-white" /></div>
              <div className="flex-1">
                <h3 className="font-heading text-lg font-semibold tracking-tight">You've identified where you're most likely to thrive</h3>
                <p className="mt-1 text-sm text-slate-500">Now turn that recommendation into a practical relocation plan. The Relocation Success Kit adds the best neighborhoods, a realistic relocation budget, a personalized moving roadmap, detailed next steps, plus taxes, logistics &amp; planning (and schools if relevant to you).</p>
              </div>
              <Button onClick={() => setShowUpsell(true)} className="h-11 gap-2 px-6 font-medium" data-testid="open-kit-upsell-btn">
                Upgrade for +$32
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="mt-8 overflow-hidden rounded-lg border border-slate-900 bg-slate-950 p-8 text-white" data-testid="unlock-cta">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-white/10"><Lock className="h-6 w-6" /></div>
            <div className="flex-1">
              <h3 className="font-heading text-2xl font-semibold tracking-tight">Unlock your Personal Relocation Report</h3>
              <p className="mt-1 text-sm text-slate-400">Your top 10 city ranking, a side-by-side comparison, a realistic monthly budget snapshot, personalized next steps, and every trade-off behind your matches — plus a downloadable PDF.</p>
            </div>
            <Button onClick={unlock} disabled={unlocking || verifyingPayment} variant="secondary" className="h-11 gap-2 px-6 font-medium" data-testid="unlock-report-btn">
              {verifyingPayment ? (<><Loader2 className="h-4 w-4 animate-spin" /> Verifying payment…</>)
                : unlocking ? <Loader2 className="h-4 w-4 animate-spin" />
                : (<><Lock className="h-4 w-4" /> Unlock for $17.99</>)}
            </Button>
          </div>
        </div>
      )}

      <Dialog open={showUpsell} onOpenChange={setShowUpsell}>
        <DialogContent data-testid="kit-upsell-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-semibold tracking-tight">
              Upgrade to the Relocation Success Kit
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              You've identified where you're most likely to thrive. For just <span className="font-semibold text-slate-900">+$32</span>,
              turn that recommendation into a practical relocation plan.
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-2.5 py-1">
            {[
              "Best neighborhoods for your profile",
              "A realistic relocation budget",
              "Your personalized moving roadmap",
              "Detailed, step-by-step next actions",
              "Schools shortlist — only if relevant to you",
              "Taxes, logistics & planning considerations",
              "30-day AI relocation advisor",
            ].map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-slate-900" /> {f}
              </li>
            ))}
          </ul>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setShowUpsell(false)} data-testid="dismiss-upsell-btn">
              Maybe later
            </Button>
            <Button onClick={startUpgrade} disabled={upgrading} className="gap-2 font-medium" data-testid="confirm-upgrade-btn">
              {upgrading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upgrade now · +$32"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
